import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { useUserState } from "../query/UserProvider";
import { useUpdateUserStateItem } from "../query/user-state/useUpdateUserStateItem";
import { isUserStateConflictError } from "../query/user-state/userStateOptimisticQuery";
import type { UserStateEntry, UserStateItemKey } from "../types/user-state";
import {
  buildProfileConflictActions,
  buildPreferencesConflictActions,
  buildSettingsConflictActions,
} from "./user-state-conflict-resolvers";
import { stableHash } from '../utils/text/stable-hash';

const SYNC_DELAY_MS = 750;

function normalizePayload<T extends Record<string, unknown>>(payload: T): Record<string, unknown> {
  return JSON.parse(JSON.stringify(payload));
}

/**
 * TEC-404: uses useUpdateUserStateItem (TanStack) exclusively — Redux's userState.entries setEntry
 * dual-write was removed in Phase C once every `state.userState.entries['profile'/'preferences'/
 * 'settings']` reader had migrated. isUserStateConflictError replaces the local
 * extractConflictEntry helper; the conflict-adoption behavior itself is unchanged (adopt the
 * server's data as "already synced" to stop an immediate re-PUT of the losing payload once the
 * source Redux slices reconcile). jobForm/comparisonPreferences/lang/ui themselves are NOT
 * migrated here and shouldn't be — they're the live editable draft state this hook watches and
 * syncs *from*, not the sync target.
 */
export interface SyncStatus {
  /** Epoch ms of the last successful sync (server accepted our write, or our write already matched the server). Null before the first sync in this session. */
  lastSyncedAt: number | null;
  /** True while a change is debounced/in-flight and hasn't been confirmed synced yet. */
  isSyncing: boolean;
}

function useSyncUserStateItem(
  itemKey: UserStateItemKey,
  payload: Record<string, unknown>,
  userId?: string | null,
  onConflict?: (data: Record<string, unknown>) => void
): SyncStatus {
  const { data: userState } = useUserState();
  const entry = userState?.[itemKey];
  const updateUserStateItem = useUpdateUserStateItem();
  const lastSyncedHashRef = useRef<string | null>(null);
  const onConflictRef = useRef(onConflict);
  onConflictRef.current = onConflict;
  const normalizedPayload = useMemo(() => normalizePayload(payload), [payload]);
  const payloadHash = useMemo(() => stableHash(normalizedPayload), [normalizedPayload]);
  const entryHash = useMemo(
    () => (entry && entry.data ? stableHash(entry.data) : null),
    [entry]
  );
  const [lastSyncedAt, setLastSyncedAt] = useState<number | null>(null);
  // Mirrors lastSyncedHashRef into render-reactive state: the ref alone is enough for the
  // synchronous debounce/dedupe logic below, but a ref mutation doesn't trigger a re-render, so
  // isSyncing/lastSyncedAt (consumed by the ProfileSheet footer's "Saved just now" line) need
  // their own state that's set at every point the ref is.
  const [syncedHash, setSyncedHash] = useState<string | null>(null);
  const markSynced = useCallback((hash: string | null) => {
    lastSyncedHashRef.current = hash;
    setSyncedHash(hash);
    if (hash !== null) setLastSyncedAt(Date.now());
  }, []);
  const isSyncing = Boolean(userId) && payloadHash !== syncedHash;

  useEffect(() => {
    if (entryHash) {
      markSynced(entryHash);
    }
  }, [entryHash, markSynced]);

  useEffect(() => {
    if (!userId) {
      lastSyncedHashRef.current = null;
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }
    if (typeof window === "undefined") {
      return;
    }

    if (payloadHash === entryHash) {
      markSynced(payloadHash);
      return;
    }

    if (payloadHash === lastSyncedHashRef.current) {
      return;
    }

    lastSyncedHashRef.current = payloadHash;

    const version = entry?.version ?? 0;

    let cancelled = false;
    const timeout = window.setTimeout(async () => {
      if (cancelled) return;
      try {
        const result = await updateUserStateItem.mutateAsync({
          itemKey,
          data: normalizedPayload,
          version,
        });
        if (!cancelled) {
          markSynced(result?.data ? stableHash(result.data) : payloadHash);
        }
      } catch (error) {
        if (isUserStateConflictError(error)) {
          const conflict = error.data as UserStateEntry;
          console.info(`[user-state] Conflict syncing ${itemKey}, adopting server version`);
          if (conflict.data) {
            onConflictRef.current?.(conflict.data as Record<string, unknown>);
          }
          // Adopting the server's data as "already synced" (rather than the
          // stale local payload) stops this effect from immediately
          // re-scheduling another PUT of the same losing payload once the
          // source slices above have been reconciled to match.
          markSynced(conflict.data ? stableHash(conflict.data) : null);
        } else {
          lastSyncedHashRef.current = null;
          setSyncedHash(null);
          console.error(`[user-state] Failed to sync ${itemKey}`, error);
        }
      }
    }, SYNC_DELAY_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [
    entry?.version,
    entryHash,
    itemKey,
    markSynced,
    normalizedPayload,
    payloadHash,
    updateUserStateItem,
    userId,
  ]);

  return { lastSyncedAt, isSyncing };
}

/**
 * Aggregated sync status across the profile item only (jobForm), used by the profile page's
 * "Saved just now" / "Saving..." status line (ProfileSheet footer, StrengthMeter). Preferences
 * and settings sync independently and don't drive this UI.
 */
export function useProfilePreferencesSettingsSync(userId?: string | null): SyncStatus {
  const dispatch = useAppDispatch();
  const jobForm = useAppSelector((state) => state.jobForm);
  const comparisonPreferences = useAppSelector((state) => state.comparisonPreferences);
  const lang = useAppSelector((state) => state.lang.value);
  const ui = useAppSelector((state) => state.ui);

  const jobFormPayload = useMemo(() => normalizePayload(jobForm), [jobForm]);
  const comparisonPreferencesPayload = useMemo(
    () => normalizePayload(comparisonPreferences),
    [comparisonPreferences]
  );
  const settingsPayload = useMemo(() => normalizePayload({ lang, ui }), [lang, ui]);

  const handleProfileConflict = useCallback(
    (data: Record<string, unknown>) => {
      buildProfileConflictActions(data).forEach((action) => dispatch(action));
    },
    [dispatch]
  );
  const handlePreferencesConflict = useCallback(
    (data: Record<string, unknown>) => {
      buildPreferencesConflictActions(data).forEach((action) => dispatch(action));
    },
    [dispatch]
  );
  const handleSettingsConflict = useCallback(
    (data: Record<string, unknown>) => {
      buildSettingsConflictActions(data).forEach((action) => dispatch(action));
    },
    [dispatch]
  );

  const profileStatus = useSyncUserStateItem("profile", jobFormPayload, userId, handleProfileConflict);
  useSyncUserStateItem("preferences", comparisonPreferencesPayload, userId, handlePreferencesConflict);
  useSyncUserStateItem("settings", settingsPayload, userId, handleSettingsConflict);

  return profileStatus;
}

